import { useEffect, useMemo, useState } from 'react'
import { collection, limit, onSnapshot, orderBy, query, where } from 'firebase/firestore'
import type { LapDoc } from '@racedash/shared'
import { db } from '../lib/firebase'
import { useReplayOf } from './replayContext'

/**
 * Laps for several cars at once, for charts that compare them.
 *
 * One listener per car rather than one query over the session. A full field at
 * a twenty-five hour race is a hundred cars and sixty thousand lap documents,
 * and nobody wants to compare a hundred lines — so this is capped, and the cap
 * is what keeps a comparison chart from quietly becoming a denial of service on
 * the person's own browser.
 *
 * Live, like every other listener here: a lap completed while a chart is open
 * extends its line.
 */
const maxCars = 6
const perCar = 1200

export function useCarsLaps(
  streamSessionId: string | null,
  cars: readonly string[],
): Map<string, LapDoc[]> {
  const [laps, setLaps] = useState<Map<string, LapDoc[]>>(new Map())

  // Primitive, so a caller building the array inline does not resubscribe on
  // every render.
  const key = cars.slice(0, maxCars).join(',')
  const replay = useReplayOf(streamSessionId)
  const replayLaps = replay?.frame.laps
  const replaying = replayLaps != null
  const replayed = useMemo(() => {
    if (!replayLaps) return null
    const wanted = key ? key.split(',') : []
    return new Map(
      wanted.map((car) => [car, replayLaps.filter((l) => l.car === car).sort((a, b) => b.lap - a.lap)] as const),
    )
  }, [replayLaps, key])

  useEffect(() => {
    const wanted = key ? key.split(',') : []
    if (!streamSessionId || wanted.length === 0 || replaying) {
      setLaps(new Map())
      return
    }

    // Rebuilt rather than merged, so a car removed from the selection has its
    // laps dropped rather than lingering as a line nobody asked for.
    const found = new Map<string, LapDoc[]>()
    const unsubs = wanted.map((car) =>
      onSnapshot(
        query(
          collection(db, 'streamSessions', streamSessionId, 'laps'),
          where('car', '==', car),
          orderBy('lap', 'desc'),
          limit(perCar),
        ),
        (snap) => {
          found.set(car, snap.docs.map((d) => d.data() as LapDoc))
          setLaps(new Map(found))
        },
        () => {
          found.set(car, [])
          setLaps(new Map(found))
        },
      ),
    )
    return () => unsubs.forEach((u) => u())
  }, [streamSessionId, key, replaying])

  return replayed ?? laps
}
